import * as React from 'react';
import { useEffect, useContext } from 'react';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import { AxiosResponse } from 'axios';
import { TffBookingSource } from '@tff/types/tff';
import { Message, Status, AdditionalChannelType } from '@tff/types/tff/message-service';
import { useKeycloak } from '@react-keycloak/web';
import { useBookingContext } from './use-booking-context';
import { apigeeEndpoint } from '../apis';

export type MessagesProps = {
  bookingSource?: TffBookingSource;
  recordLocator?: string;
};

export type TypeOfStatus = 'IN_FETCHING' | 'FETCHED' | 'FAILURE';

export type MessagesResponse = {
  messageServiceItems?: Message[];
  messageServiceStatus?: TypeOfStatus;
  messageServiceError?: string;
  resetMessageServiceState: () => void;
  addMessage: (Message) => void;
  sendMessage: (
    type: 'LOG_ENTRY' | 'NOTICE',
    action: string,
    status: Status,
    source?: string,
    content?: string,
    additionalChannel?: AdditionalChannelType,
  ) => void;
};

//
export const useMessages = (messagesProps?: MessagesProps): MessagesResponse => {
  const [messageServiceStatus, setMessageServiceStatus] = React.useState<TypeOfStatus | undefined>(undefined);
  const [messageServiceItems, setMessageServiceItems] = React.useState<Message[] | undefined>(undefined);
  const [messageServiceError, setMessageServiceError] = React.useState<string | undefined>(undefined);
  const { showLoading, closeLoading } = useContext(LoadingContext);

  // Get context for helper methods
  const { keycloak } = useKeycloak();
  const { recordLocator, bookingSource } = useBookingContext();

  const storeMessageAtBackend = async (message: Message): Promise<void> => {
    try {
      await apigeeEndpoint.post(`/message`, message);
      setMessageServiceItems(old => [...(old ?? []), message]);
    } catch (error) {
      if (error.isAxiosError) {
        setMessageServiceError(error.toJSON());
      } else {
        setMessageServiceError(error);
      }
      setMessageServiceStatus('FAILURE');
    }
  };

  const addMessage = (message: Message): void => {
    void storeMessageAtBackend(message);
  };

  // Helper methods for consistent message creation
  const createMessage = (
    type: 'LOG_ENTRY' | 'NOTICE',
    action: string,
    status: Status,
    source?: string,
    content?: string,
    additionalChannel?: AdditionalChannelType,
  ): Message => {
    return {
      createdBy: keycloak?.profile?.email ?? 'UNKNOWN',
      recordLocator: messagesProps?.recordLocator ?? recordLocator ?? 'UNKNOWN',
      type,
      source: source ?? messagesProps?.bookingSource ?? bookingSource ?? 'UNKNOWN',
      action,
      status,
      additionalChannel,
      content,
    };
  };

  const sendMessage = (
    type: 'LOG_ENTRY' | 'NOTICE',
    action: string,
    status: Status,
    source?: string,
    content?: string,
    additionalChannel?: AdditionalChannelType,
  ): void => {
    addMessage(createMessage(type, action, status, source, content, additionalChannel));
  };

  const resetMessageServiceState = (): void => {
    setMessageServiceStatus(undefined);
    setMessageServiceItems(undefined);
    setMessageServiceError(undefined);
  };

  useEffect(() => {
    const fetchDataFromBackend = async (
      recordLocator: string,
      bookingSource?: TffBookingSource | 'TUI-NDC-SQ',
    ): Promise<void> => {
      try {
        showLoading('Search messages for RecordLocator: ');
        setMessageServiceStatus('IN_FETCHING');

        const response: AxiosResponse<Message[]> = await apigeeEndpoint.get<Message[]>(
          `/message/${bookingSource}/${recordLocator}`,
        );

        if ('errorMessage' in response.data) {
          throw response.data['errorMessage'];
        }

        setMessageServiceItems(response.data);
        setMessageServiceStatus('FETCHED');
      } catch (error) {
        setMessageServiceError(error);
        setMessageServiceStatus('FAILURE');
      } finally {
        closeLoading();
      }
    };

    if (messagesProps?.recordLocator && messagesProps?.bookingSource && !messageServiceStatus) {
      void fetchDataFromBackend(messagesProps.recordLocator, messagesProps.bookingSource);
    }
  }, [messagesProps]);

  return {
    messageServiceItems,
    messageServiceStatus,
    messageServiceError,
    resetMessageServiceState,
    addMessage,
    sendMessage,
  };
};
